import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import NewsCard from '@/components/NewsCard'
import type { NewsArticle } from '@/types'
import {Loader2, ArrowLeft} from 'lucide-react'
import { trackArticleView, trackPageView } from '@/lib/trackPageView'

const SITE_URL = 'https://carlosfelipeofera.com'
const SITE_NAME = 'CARLOS FELIPE'

/** Injeta/atualiza uma <meta> pelo atributo de seleção */
function setMeta(selector: string, attr: string, content: string) {
  if (!content) return
  let el = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    const [attrName, attrValue] = selector.replace('meta[', '').replace(']', '').split('=')
    el.setAttribute(attrName.trim(), attrValue.replace(/"/g, '').trim())
    document.head.appendChild(el)
  }
  el.setAttribute(attr, content)
}

/** Injeta/atualiza uma <link> pelo rel */
function setLink(rel: string, href: string) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function injectOgTags(article: NewsArticle, spaUrl: string) {
  const title = article.metaTitle || article.title
  const desc = (article.metaDescription || article.excerpt || article.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()).slice(0, 250)
  const image = article.ogImage || article.imageUrl || ''

  // Título e descrição padrão
  document.title = `${title} | ${SITE_NAME}`
  setMeta('meta[name="description"]', 'content', desc)

  // Canonical
  setLink('canonical', spaUrl)

  // Open Graph
  setMeta('meta[property="og:type"]',        'content', 'article')
  setMeta('meta[property="og:site_name"]',   'content', SITE_NAME)
  setMeta('meta[property="og:title"]',       'content', title)
  setMeta('meta[property="og:description"]', 'content', desc)
  setMeta('meta[property="og:url"]',         'content', spaUrl)
  setMeta('meta[property="og:image"]',       'content', image)
  setMeta('meta[property="og:image:width"]', 'content', '1200')
  setMeta('meta[property="og:image:height"]','content', '630')
  setMeta('meta[property="og:locale"]',      'content', 'pt_BR')

  // Twitter / X Cards
  setMeta('meta[name="twitter:card"]',        'content', 'summary_large_image')
  setMeta('meta[name="twitter:title"]',       'content', title)
  setMeta('meta[name="twitter:description"]', 'content', desc)
  setMeta('meta[name="twitter:image"]',       'content', image)
  setMeta('meta[name="twitter:site"]',        'content', '@CarlosFelipeOF')
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<NewsArticle | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return }

    setLoading(true)
    lumi.entities.news_articles.list({
      filter: { slug, status: 'published' },
      limit: 1,
    }).then((res: any) => {
      const found = res?.list?.[0] as NewsArticle | undefined
      if (found) {
        setArticle(found)
        const articleSlug = found.slug || found._id
        // Prefere o domínio real; cai no origin atual se ainda não configurado
        const origin = SITE_URL || window.location.origin
        const spaUrl = `${origin}/artigo/${encodeURIComponent(articleSlug)}`
        injectOgTags(found, spaUrl)
        trackPageView('article')
        trackArticleView(found._id, found.views)
      } else {
        // Tenta pelo _id como fallback
        return lumi.entities.news_articles.list({ limit: 200 }).then((r: any) => {
          const byId = (r?.list || []).find((a: any) => a._id === slug)
          if (byId) {
            setArticle(byId)
            const origin = SITE_URL || window.location.origin
            const spaUrl = `${origin}/artigo/${encodeURIComponent(byId.slug || byId._id)}`
            injectOgTags(byId, spaUrl)
            trackPageView('article')
            trackArticleView(byId._id, byId.views)
          } else {
            setNotFound(true)
          }
        })
      }
    }).catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [slug])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Header onSearch={(q) => navigate(q ? `/?busca=${encodeURIComponent(q)}` : '/')} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate('/')}
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <ArrowLeft size={15} />
          Voltar às notícias
        </button>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 size={40} className="text-red-600 animate-spin" />
            <p className="text-gray-500 dark:text-gray-400">A carregar artigo…</p>
          </div>
        )}

        {!loading && notFound && (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">Artigo não encontrado</p>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-red-600 hover:underline"
            >
              Ir para a página inicial
            </button>
          </div>
        )}

        {!loading && article && <NewsCard article={article} />}
      </main>

      <Footer />
    </div>
  )
}
